/**
 * Cognito permission groups that unlock the Statistics page. Single source for
 * the nav gating (useNavData) and the in-page guard (StatisticsPage) — and it
 * must stay in sync with the @AnyRole guards on the BE /orders/stats*
 * endpoints (orders.controller.ts STATS_PAGE_ROLES).
 */
export const STATISTICS_PERMISSIONS = [
  'BACK_OFFICE',
  'VIEW_ORDER',
  'INSERT_ORDER',
  'DISTRIBUTOR',
  'ADMIN',
];

/**
 * Company-wide statistics — the by-zone sales table, the business-area rollup
 * and any `scope=company` request — are limited to ADMIN / BACK_OFFICE.
 *
 * VIEW_ORDER is deliberately NOT enough. Agents need VIEW_ORDER to read the
 * order list they write into, so treating it as "management" handed every agent
 * the whole company's numbers (measured on stage: 4 of 5 users carried
 * VIEW_ORDER). Back office therefore gets its own group.
 *
 * This is the cosmetic FE gate; the BE enforces the same rule in the services
 * against the DB permissions (src/orders/stats-access.ts).
 */
export const canSeeCompanyStats = (permissions: string[]): boolean =>
  permissions.includes('ADMIN') || permissions.includes('BACK_OFFICE');

/**
 * The budget block is the one exception to the company-only rule: the BE clips
 * both sides of the comparison (actuals and the ERP budget header) to the
 * caller's own zones, so agents (INSERT_ORDER) and back-office readers
 * (VIEW_ORDER) see their own zones' target without any company-wide figure
 * leaking. Distributors stay excluded.
 * Mirrors hasBudgetStatsAccess in the BE's stats-access.ts.
 */
export const canSeeBudgetStats = (permissions: string[]): boolean =>
  canSeeCompanyStats(permissions) ||
  permissions.includes('VIEW_ORDER') ||
  permissions.includes('INSERT_ORDER');

/*
 * There is deliberately NO money predicate here. Whether the breakdown carries
 * money is decided by the BE alone (hasMoneyStatsAccess in stats-access.ts: a
 * DISTRIBUTOR without ADMIN/BACK_OFFICE gets the legacy units-only shape), and
 * the widgets are already data-driven about it — `hasValueMetric` hides the €
 * toggle when no bucket carries `value`, and the Colors card is mounted only
 * when `byColor` is present. Mirroring the rule here would add a second place
 * to keep in sync for no behavioural gain.
 */
